// components/Table/Menus.tsx
import ContextMenu from "@/components/ContextMenu";
import { Button } from "@/components/ui/Button";
import { useState, useEffect, useRef } from "react";
import { useTheme } from "@/hooks/useTheme";
import { resolveColor } from "./utils";
import { operatorUnicodeMap } from "@/constants/operators";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import ThemedInput from "@/components/ThemedInput";
import { ThemedText } from "@/components/ThemedText";
import { FipsPicker } from "@/components/Pickers/FipsPicker";
import { MultiMarketPicker } from "@/components/Pickers/MultiMarketPicker";
import { TimeModifierEditor } from "@/components/Editors/TimeModifierEditor";
import { TimeModifierCode } from "@/types/db/time_cards";

import {
  FieldSpec,
  FieldFilter,
  Expression,
  Visible as GVisible,
  Order as GOrder,
  Operator,
  FieldKind,
  DEFAULT_OPS,
  getDisplayName,
  displayName,
  MatchMode,
  EnumOption,
  LinkedRecordConfig,
} from "./types";

// ---------- ExpressionEditor ----------
// Popover content for managing expressions on a field
function ExpressionEditor({
  kind,
  expressions,
  onChange,
  matchMode = "all",
  onMatchModeChange,
  options,
  linkedRecord,
}: {
  kind: FieldKind;
  expressions: Expression[];
  onChange: (expressions: Expression[]) => void;
  matchMode?: MatchMode;
  onMatchModeChange?: (mode: MatchMode) => void;
  options?: EnumOption[];
  linkedRecord?: LinkedRecordConfig;
}) {
  const { colors } = useTheme();
  const allowedOps = DEFAULT_OPS[kind];
  const [opMenuOpen, setOpMenuOpen] = useState<number | null>(null);
  const opBtnRefs = useRef<Record<number, View | null>>({});

  const addExpression = () => {
    if (kind === "linked_record") {
      onChange([
        ...expressions,
        { operator: "like" as Operator, value: "", subfield: linkedRecord?.labelKey || "name" },
      ]);
      return;
    }
    if (kind === "iso_week") {
      onChange([...expressions, { operator: "eq" as Operator, value: "", subfield: "week" }]);
      return;
    }
    // For enum and array with in/nin, default to "in" with empty array
    const defaultOp = kind === "enum" || kind === "array" ? "in" : kind === "text" ? "like" : "eq";
    const defaultValue = kind === "enum" || kind === "array" ? [] : "";
    onChange([...expressions, { operator: defaultOp, value: defaultValue }]);
  };

  const updateExpression = (index: number, expr: Expression) => {
    const updated = [...expressions];
    updated[index] = expr;
    onChange(updated);
  };

  const removeExpression = (index: number) => {
    onChange(expressions.filter((_, i) => i !== index));
  };

  // For enum fields, check if operator is multi-select type
  const isMultiSelect = (op: string) => op === "in" || op === "nin";

  // Handle enum value toggle for multi-select (in/nin)
  const toggleEnumValue = (index: number, value: string) => {
    const expr = expressions[index];
    const currentValues = Array.isArray(expr.value) ? expr.value : [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    updateExpression(index, { ...expr, value: newValues });
  };

  // Handle enum value select for single-select (eq/neq)
  const selectEnumValue = (index: number, value: string) => {
    updateExpression(index, { ...expressions[index], value });
  };

  // Handle operator change for enum - reset value when switching between single/multi
  const handleEnumOperatorChange = (index: number, newOp: string) => {
    const expr = expressions[index];
    const wasMulti = isMultiSelect(expr.operator);
    const isNowMulti = isMultiSelect(newOp);

    let newValue = expr.value;
    if (wasMulti && !isNowMulti) {
      // Multi -> Single: take first value or empty string
      newValue = Array.isArray(expr.value) && expr.value.length > 0 ? expr.value[0] : "";
    } else if (!wasMulti && isNowMulti) {
      // Single -> Multi: wrap in array
      newValue = expr.value ? [expr.value] : [];
    }

    updateExpression(index, { ...expr, operator: newOp as Operator, value: newValue });
    setOpMenuOpen(null);
  };

  // Render enum expression UI
  const renderEnumExpression = (expr: Expression, i: number) => {
    const multiSelect = isMultiSelect(expr.operator);
    const selectedValues = multiSelect ? (Array.isArray(expr.value) ? expr.value : []) : [expr.value?.toString() || ""];

    return (
      <View key={i} style={[styles.enumExpressionContainer, opMenuOpen === i && { zIndex: 1, elevation: 1 }]}>
        {/* Operator dropdown */}
        <View style={[styles.enumOperatorRow, opMenuOpen === i && { elevation: 1, zIndex: 1 }]}>
          <View style={{ position: "relative" }}>
            <Button
              ref={(el) => {
                opBtnRefs.current[i] = el;
              }}
              style={styles.operatorButton}
              icon={operatorUnicodeMap[expr.operator]?.icon}
              text={operatorUnicodeMap[expr.operator]?.label ?? expr.operator}
              textStyle={{ fontSize: 12 }}
              onPress={() => setOpMenuOpen(opMenuOpen === i ? null : i)}
            />
            <ContextMenu
              style={{ top: 32, left: 0, width: 150, zIndex: 10 }}
              visible={opMenuOpen === i}
              setVisible={() => setOpMenuOpen(null)}
              ignoreRef={{ current: opBtnRefs.current[i] }}
            >
              {allowedOps.map((op) => (
                <Button
                  key={op}
                  style={styles.menuButton}
                  icon={operatorUnicodeMap[op]?.icon}
                  iconSize={14}
                  text={operatorUnicodeMap[op]?.label ?? op}
                  textStyle={{ fontSize: 12 }}
                  onPress={() => handleEnumOperatorChange(i, op)}
                />
              ))}
            </ContextMenu>
          </View>
          <Button
            icon="close"
            iconSize={14}
            iconStyle={{ color: colors.textSecondary }}
            style={{ borderWidth: 0, backgroundColor: "transparent", minWidth: 28, maxHeight: 28 }}
            onPress={() => removeExpression(i)}
          />
        </View>

        {/* Enum options grid */}
        <View style={styles.enumOptionsGrid}>
          {options?.map((opt) => {
            const isSelected = selectedValues.includes(opt.value);
            const optColor = resolveColor(opt.color, colors);
            const fillColor = opt.color ? optColor : colors.primary;
            return (
              <Button
                key={opt.value}
                text={opt.label || opt.value}
                style={[
                  styles.enumOptionButton,
                  isSelected ? { backgroundColor: fillColor, borderColor: fillColor } : { borderColor: colors.border },
                ]}
                textStyle={[
                  styles.enumOptionText,
                  isSelected ? { color: "white", fontWeight: "600" } : { color: colors.placeholder },
                ]}
                onPress={() => (multiSelect ? toggleEnumValue(i, opt.value) : selectEnumValue(i, opt.value))}
              />
            );
          })}
        </View>

        {/* Selection count for multi-select */}
        {multiSelect && selectedValues.filter(Boolean).length > 0 && (
          <ThemedText type="small" style={{ color: colors.textSecondary, marginTop: 4 }}>
            {selectedValues.filter(Boolean).length} selected
          </ThemedText>
        )}
      </View>
    );
  };

  // For enum and array fields with options, render specialized UI
  if ((kind === "enum" || kind === "array") && options && options.length > 0) {
    return (
      <View style={styles.expressionEditor}>
        {expressions.length === 0 ? (
          <ThemedText type="small" style={{ color: colors.textSecondary, marginBottom: 8 }}>
            Add a condition to filter by this field
          </ThemedText>
        ) : (
          expressions.map((expr, i) => renderEnumExpression(expr, i))
        )}
        <Button
          icon="plus"
          text="Add condition"
          style={styles.addConditionButton}
          textStyle={{ fontSize: 12, color: colors.textSecondary }}
          iconColor={colors.textSecondary}
          onPress={addExpression}
        />
      </View>
    );
  }

  // ISO week expression editor with Part toggle (Week/Year)
  if (kind === "iso_week") {
    return (
      <View style={styles.expressionEditor}>
        <View style={styles.matchModeRow}>
          <ThemedText type="small" style={{ color: colors.textSecondary }}>
            Match:
          </ThemedText>
          <Button
            text="All"
            style={[
              styles.matchModeButton,
              matchMode === "all" && { backgroundColor: colors.primary, borderColor: colors.primary },
            ]}
            textStyle={[styles.matchModeText, matchMode === "all" && { color: "white" }]}
            onPress={() => onMatchModeChange?.("all")}
          />
          <Button
            text="Any"
            style={[
              styles.matchModeButton,
              matchMode === "any" && { backgroundColor: colors.primary, borderColor: colors.primary },
            ]}
            textStyle={[styles.matchModeText, matchMode === "any" && { color: "white" }]}
            onPress={() => onMatchModeChange?.("any")}
          />
        </View>
        {expressions.map((expr, i) => (
          <View key={i} style={[styles.expressionRow, opMenuOpen === i && { elevation: 1, zIndex: 1 }]}>
            {/* Part toggle (Week / Year) */}
            <Button
              text={expr.subfield === "year" ? "Year" : "Week"}
              style={styles.operatorButton}
              textStyle={{ fontSize: 12 }}
              onPress={() => {
                const newSubfield = expr.subfield === "year" ? "week" : "year";
                updateExpression(i, { ...expr, subfield: newSubfield });
              }}
            />

            {/* Operator dropdown */}
            <View style={{ position: "relative" }}>
              <Button
                ref={(el) => {
                  opBtnRefs.current[i] = el;
                }}
                style={styles.operatorButton}
                icon={operatorUnicodeMap[expr.operator]?.icon}
                iconSize={18}
                textStyle={{ fontSize: 12 }}
                onPress={() => setOpMenuOpen(opMenuOpen === i ? null : i)}
              />
              <ContextMenu
                style={{ top: 32, left: 0, width: 150, zIndex: 10 }}
                visible={opMenuOpen === i}
                setVisible={() => setOpMenuOpen(null)}
                ignoreRef={{ current: opBtnRefs.current[i] }}
              >
                {allowedOps.map((op) => (
                  <Button
                    key={op}
                    style={styles.menuButton}
                    icon={operatorUnicodeMap[op]?.icon}
                    iconSize={14}
                    text={operatorUnicodeMap[op]?.label ?? op}
                    textStyle={{ fontSize: 12 }}
                    onPress={() => {
                      updateExpression(i, { ...expr, operator: op });
                      setOpMenuOpen(null);
                    }}
                  />
                ))}
              </ContextMenu>
            </View>

            {/* Value input */}
            <ThemedInput
              value={expr.value?.toString() || ""}
              inputStyle={{ minHeight: 28, maxHeight: 28, fontSize: 13 }}
              style={{ flex: 1, minHeight: 28, maxHeight: 28 }}
              placeholder={expr.subfield === "year" ? "2026" : "1-53"}
              keyboardType="numeric"
              onChangeText={(v) => updateExpression(i, { ...expr, value: v.replace(/[^0-9]/g, "") })}
            />

            {/* Remove button */}
            <Button
              icon="close"
              iconSize={14}
              iconStyle={{ color: colors.textSecondary }}
              style={{ borderWidth: 0, backgroundColor: "transparent", minWidth: 28, maxHeight: 28 }}
              onPress={() => removeExpression(i)}
            />
          </View>
        ))}
        <Button
          icon="plus"
          text="Add condition"
          style={styles.addConditionButton}
          textStyle={{ fontSize: 12, color: colors.textSecondary }}
          iconColor={colors.textSecondary}
          onPress={addExpression}
        />
      </View>
    );
  }

  // Linked record expression editor with Part toggle (Name/Id)
  if (kind === "linked_record") {
    const labelKey = linkedRecord?.labelKey || "name";
    const valueKey = linkedRecord?.valueKey || "id";

    return (
      <View style={styles.expressionEditor}>
        <View style={styles.matchModeRow}>
          <ThemedText type="small" style={{ color: colors.textSecondary }}>
            Match:
          </ThemedText>
          <Button
            text="All"
            style={[
              styles.matchModeButton,
              matchMode === "all" && { backgroundColor: colors.primary, borderColor: colors.primary },
            ]}
            textStyle={[styles.matchModeText, matchMode === "all" && { color: "white" }]}
            onPress={() => onMatchModeChange?.("all")}
          />
          <Button
            text="Any"
            style={[
              styles.matchModeButton,
              matchMode === "any" && { backgroundColor: colors.primary, borderColor: colors.primary },
            ]}
            textStyle={[styles.matchModeText, matchMode === "any" && { color: "white" }]}
            onPress={() => onMatchModeChange?.("any")}
          />
        </View>
        {expressions.map((expr, i) => (
          <View key={i} style={[styles.expressionRow, opMenuOpen === i && { elevation: 1, zIndex: 1 }]}>
            {/* Part toggle (Name / Id) */}
            <Button
              text={displayName(expr.subfield || labelKey)}
              style={styles.operatorButton}
              textStyle={{ fontSize: 12 }}
              onPress={() => {
                const currentSubfield = expr.subfield || labelKey;
                const newSubfield = currentSubfield === labelKey ? valueKey : labelKey;
                updateExpression(i, { ...expr, subfield: newSubfield });
              }}
            />

            {/* Operator dropdown */}
            <View style={{ position: "relative" }}>
              <Button
                ref={(el) => {
                  opBtnRefs.current[i] = el;
                }}
                style={styles.operatorButton}
                icon={operatorUnicodeMap[expr.operator]?.icon}
                iconSize={18}
                textStyle={{ fontSize: 12 }}
                onPress={() => setOpMenuOpen(opMenuOpen === i ? null : i)}
              />
              <ContextMenu
                style={{ top: 32, left: 0, width: 150, zIndex: 10 }}
                visible={opMenuOpen === i}
                setVisible={() => setOpMenuOpen(null)}
                ignoreRef={{ current: opBtnRefs.current[i] }}
              >
                {allowedOps.map((op) => (
                  <Button
                    key={op}
                    style={styles.menuButton}
                    icon={operatorUnicodeMap[op]?.icon}
                    iconSize={14}
                    text={operatorUnicodeMap[op]?.label ?? op}
                    textStyle={{ fontSize: 12 }}
                    onPress={() => {
                      updateExpression(i, { ...expr, operator: op });
                      setOpMenuOpen(null);
                    }}
                  />
                ))}
              </ContextMenu>
            </View>

            {/* Value input */}
            <ThemedInput
              value={expr.value?.toString() || ""}
              inputStyle={{ minHeight: 28, maxHeight: 28, fontSize: 13 }}
              style={{ flex: 1, minHeight: 28, maxHeight: 28 }}
              placeholder="Value"
              onChangeText={(v) => updateExpression(i, { ...expr, value: v })}
            />

            {/* Remove button */}
            <Button
              icon="close"
              iconSize={14}
              iconStyle={{ color: colors.textSecondary }}
              style={{ borderWidth: 0, backgroundColor: "transparent", minWidth: 28, maxHeight: 28 }}
              onPress={() => removeExpression(i)}
            />
          </View>
        ))}
        <Button
          icon="plus"
          text="Add condition"
          style={styles.addConditionButton}
          textStyle={{ fontSize: 12, color: colors.textSecondary }}
          iconColor={colors.textSecondary}
          onPress={addExpression}
        />
      </View>
    );
  }

  // Standard expression editor for non-enum fields
  return (
    <View style={styles.expressionEditor}>
      <View style={styles.matchModeRow}>
        <ThemedText type="small" style={{ color: colors.textSecondary }}>
          Match:
        </ThemedText>
        <Button
          text="All"
          style={[
            styles.matchModeButton,
            matchMode === "all" && { backgroundColor: colors.primary, borderColor: colors.primary },
          ]}
          textStyle={[styles.matchModeText, matchMode === "all" && { color: "white" }]}
          onPress={() => onMatchModeChange?.("all")}
        />
        <Button
          text="Any"
          style={[
            styles.matchModeButton,
            matchMode === "any" && { backgroundColor: colors.primary, borderColor: colors.primary },
          ]}
          textStyle={[styles.matchModeText, matchMode === "any" && { color: "white" }]}
          onPress={() => onMatchModeChange?.("any")}
        />
      </View>
      {expressions.map((expr, i) => (
        <View key={i} style={[styles.expressionRow, opMenuOpen === i && { elevation: 1, zIndex: 1 }]}>
          {/* Operator dropdown */}
          <View style={{ position: "relative" }}>
            <Button
              ref={(el) => {
                opBtnRefs.current[i] = el;
              }}
              style={styles.operatorButton}
              icon={operatorUnicodeMap[expr.operator]?.icon}
              iconSize={18}
              // text={operatorUnicodeMap[expr.operator]?.symbol ?? expr.operator}
              textStyle={{ fontSize: 12 }}
              onPress={() => setOpMenuOpen(opMenuOpen === i ? null : i)}
            />
            <ContextMenu
              style={{ top: 32, left: 0, width: 150, zIndex: 10 }}
              visible={opMenuOpen === i}
              setVisible={() => setOpMenuOpen(null)}
              ignoreRef={{ current: opBtnRefs.current[i] }}
            >
              {allowedOps.map((op) => (
                <Button
                  key={op}
                  style={styles.menuButton}
                  icon={operatorUnicodeMap[op]?.icon}
                  iconSize={14}
                  text={operatorUnicodeMap[op]?.label ?? op}
                  textStyle={{ fontSize: 12 }}
                  onPress={() => {
                    updateExpression(i, { ...expr, operator: op });
                    setOpMenuOpen(null);
                  }}
                />
              ))}
            </ContextMenu>
          </View>

          {/* Value input */}
          <ThemedInput
            value={expr.value?.toString() || ""}
            inputStyle={{ minHeight: 28, maxHeight: 28, fontSize: 13 }}
            style={{ flex: 1, minHeight: 28, maxHeight: 28 }}
            placeholder="Value"
            onChangeText={(v) => updateExpression(i, { ...expr, value: v })}
          />

          {/* Remove button */}
          <Button
            icon="close"
            iconSize={14}
            iconStyle={{ color: colors.textSecondary }}
            style={{ borderWidth: 0, backgroundColor: "transparent", minWidth: 28, maxHeight: 28 }}
            onPress={() => removeExpression(i)}
          />
        </View>
      ))}
      <Button
        icon="plus"
        text="Add condition"
        style={styles.addConditionButton}
        textStyle={{ fontSize: 12, color: colors.textSecondary }}
        iconColor={colors.textSecondary}
        onPress={addExpression}
      />
    </View>
  );
}

// ---------- FilterPill ----------
// Individual field pill with badge showing expression count
function FilterPill<FN extends string>({
  field,
  expressions,
  onExpressionsChange,
  matchMode,
  onMatchModeChange,
  menuToggle,
}: {
  field: FieldSpec<FN>;
  expressions: Expression[];
  onExpressionsChange: (expressions: Expression[]) => void;
  matchMode?: MatchMode;
  onMatchModeChange?: (mode: MatchMode) => void;
  menuToggle: (p: boolean) => void;
}) {
  const { colors } = useTheme();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const pillRef = useRef<View>(null);
  const hasExpressions = expressions.length > 0;

  const handleOpenPopover = () => {
    setPopoverOpen((prev) => {
      menuToggle(!prev);
      return !prev;
    });
  };

  const handleClosePopover = () => {
    setPopoverOpen(false);
    menuToggle(false);
  };

  return (
    <View style={[{ position: "relative" }, popoverOpen && { elevation: 1, zIndex: 1 }]}>
      <Button
        ref={pillRef}
        icon={hasExpressions ? "filter" : "filter-outline"}
        iconColor={hasExpressions ? colors.primary : colors.textSecondary}
        text={getDisplayName(field)}
        textStyle={[styles.pillText, { color: hasExpressions ? colors.text : colors.textSecondary }]}
        style={[styles.filterPill, { borderColor: hasExpressions ? colors.primary : colors.border }]}
        onPress={handleOpenPopover}
      />
      {hasExpressions && (
        <View style={[styles.expressionBadge, { backgroundColor: colors.primary }]}>
          <ThemedText style={styles.badgeText}>{expressions.length}</ThemedText>
        </View>
      )}
      <ContextMenu
        style={{ top: 32, left: 0, minWidth: 280, zIndex: 10 }}
        visible={popoverOpen}
        setVisible={handleClosePopover}
        ignoreRef={pillRef}
      >
        <ExpressionEditor
          kind={field.kind}
          expressions={expressions}
          onChange={onExpressionsChange}
          matchMode={matchMode}
          onMatchModeChange={onMatchModeChange}
          options={field.options}
          linkedRecord={field.linkedRecord}
        />
      </ContextMenu>
    </View>
  );
}

// ---------- FilterMenu ----------
export function FilterMenu<FN extends string>({
  initial = [],
  choices,
  menuToggle,
  onChange,
  filterMatchMode = "all",
  onFilterMatchModeChange,
}: {
  initial?: FieldFilter<FN>[];
  choices: readonly FieldSpec<FN>[];
  menuToggle: (p: boolean) => void;
  onChange?: (filters: FieldFilter<FN>[]) => void;
  filterMatchMode?: MatchMode;
  onFilterMatchModeChange?: (mode: MatchMode) => void;
}) {
  const { colors } = useTheme();
  // Initialize filters for all fields (empty expressions by default)
  const [filters, setFilters] = useState<FieldFilter<FN>[]>(() =>
    choices.map((c) => {
      const existing = initial.find((f) => f.name === c.name);
      return existing || { name: c.name, kind: c.kind, expressions: [] };
    }),
  );
  const [elevation, setElevation] = useState(-1);

  // Wrapper for menuToggle that also updates local elevation
  const handleMenuToggle = (isOpen: boolean) => {
    setElevation(isOpen ? 1 : -1);
    menuToggle(isOpen);
  };

  useEffect(() => {
    setFilters(
      choices.map((c) => {
        const existing = initial.find((f) => f.name === c.name);
        return existing || { name: c.name, kind: c.kind, expressions: [] };
      }),
    );
  }, [initial, choices]);

  const updateExpressions = (name: FN, expressions: Expression[]) => {
    const newFilters = filters.map((f) => (f.name === name ? { ...f, expressions } : f));
    setFilters(newFilters);
    onChange?.(newFilters);
  };

  const updateFieldMatchMode = (name: FN, mode: MatchMode) => {
    const newFilters = filters.map((f) => (f.name === name ? { ...f, matchMode: mode } : f));
    setFilters(newFilters);
    onChange?.(newFilters);
  };

  return (
    <View style={[styles.filterMenuContainer, { elevation }]}>
      {/* Global filter match mode toggle */}
      <View style={styles.globalMatchModeRow}>
        <ThemedText type="small" style={{ color: colors.textSecondary }}>
          Combine filters:
        </ThemedText>
        <Button
          text="All"
          style={[
            styles.matchModeButton,
            filterMatchMode === "all" && { backgroundColor: colors.primary, borderColor: colors.primary },
          ]}
          textStyle={[styles.matchModeText, filterMatchMode === "all" && { color: "white" }]}
          onPress={() => onFilterMatchModeChange?.("all")}
        />
        <Button
          text="Any"
          style={[
            styles.matchModeButton,
            filterMatchMode === "any" && { backgroundColor: colors.primary, borderColor: colors.primary },
          ]}
          textStyle={[styles.matchModeText, filterMatchMode === "any" && { color: "white" }]}
          onPress={() => onFilterMatchModeChange?.("any")}
        />
      </View>
      {choices.map((field) => {
        const filter = filters.find((f) => f.name === field.name);
        return (
          <FilterPill
            key={field.name}
            field={field}
            expressions={filter?.expressions || []}
            onExpressionsChange={(expressions) => updateExpressions(field.name, expressions)}
            matchMode={filter?.matchMode}
            onMatchModeChange={(mode) => updateFieldMatchMode(field.name, mode)}
            menuToggle={handleMenuToggle}
          />
        );
      })}
    </View>
  );
}

// ---------- VisibleMenu ----------
export function VisibleMenu<FN extends string>({
  initial = [],
  choices,
  menuToggle,
  onChange,
}: {
  initial?: GVisible<FN>[];
  choices: readonly FieldSpec<FN>[];
  menuToggle: (p: boolean) => void;
  onChange?: (visible: GVisible<FN>[]) => void;
}) {
  const [visible, setVisible] = useState<GVisible<FN>[]>(
    choices.map((c) => {
      const existing = initial.find((v) => v.name === c.name);
      return existing || { name: c.name, visible: true };
    }),
  );

  useEffect(() => {
    const next = choices.map((c) => {
      const existing = initial.find((v) => v.name === c.name);
      return existing || { name: c.name, visible: true };
    });
    setVisible((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [initial, choices]);

  const toggleVisible = (name: FN) => {
    const newVisible = visible.map((v) => (v.name === name ? { ...v, visible: !v.visible } : v));
    setVisible(newVisible);
    onChange?.(newVisible);
  };

  return (
    <View style={styles.visibleMenuContainer}>
      {choices.map((field) => {
        const isVisible = visible.find((v) => v.name === field.name)?.visible ?? true;
        return (
          <VisiblePill
            key={field.name}
            name={field.name}
            displayName={getDisplayName(field)}
            active={isVisible}
            onToggle={() => toggleVisible(field.name)}
          />
        );
      })}
    </View>
  );
}

export function VisiblePill({
  name,
  displayName,
  active = true,
  onToggle,
}: {
  name: string;
  displayName?: string;
  active?: boolean;
  onToggle?: () => void;
}) {
  const { colors } = useTheme();

  return (
    <Button
      icon={active ? "eye" : "eye-off"}
      iconColor={active ? colors.primary : colors.textSecondary}
      text={displayName || name}
      textStyle={[
        styles.pillText,
        { color: active ? colors.text : colors.textSecondary },
        !active && styles.pillTextInactive,
      ]}
      style={[
        styles.visiblePill,
        {
          backgroundColor: active ? "transparent" : "transparent", //TODO: consider inactive bg color
          borderColor: active ? colors.primary : colors.border,
        },
      ]}
      onPress={onToggle}
    />
  );
}

// ---------- OrderByMenu ----------
export function OrderByMenu<FN extends string>({
  initial,
  choices,
  menuToggle,
  onChange,
}: {
  initial?: GOrder<FN>;
  choices: { name: string; displayName?: string }[];
  menuToggle: (p?: boolean) => void;
  onChange?: (order?: GOrder<FN>) => void;
}) {
  const [expOpen, setExpOpen] = useState(false);
  const [fieldOpen, setFieldOpen] = useState(false);
  const [choice, setChoice] = useState<GOrder<FN> | undefined>(initial);
  const { colors } = useTheme();
  const fieldBtnRef = useRef<View>(null);
  const dirBtnRef = useRef<View>(null);

  useEffect(() => {
    setChoice(initial);
  }, [initial]);

  const menuOpen = fieldOpen || expOpen;

  // Get display name for current choice
  const getChoiceDisplayName = (name?: string) => {
    if (!name) return "Select field";
    const found = choices.find((c) => c.name === name);
    return found?.displayName || name;
  };

  const handlePickField = (field: string) => {
    const newChoice = {
      name: field as FN,
      direction: choice?.direction || ("ascending" as const),
    };
    setChoice(newChoice);
    onChange?.(newChoice);
    setFieldOpen(false);
    menuToggle(false);
  };

  const handlePickDirection = (direction: "ascending" | "descending") => {
    if (choice?.name) {
      const newChoice = { ...choice, direction };
      setChoice(newChoice);
      onChange?.(newChoice);
    }
    setExpOpen(false);
    menuToggle(false);
  };

  const handleClear = () => {
    setChoice(undefined);
    onChange?.(undefined);
  };

  return (
    <View style={[{ flexDirection: "row", maxHeight: 30, flexBasis: "100%" }, menuOpen && { elevation: 1, zIndex: 1 }]}>
      <Button
        ref={fieldBtnRef}
        style={{
          backgroundColor: "transparent",
          flexGrow: 1,
          flexBasis: "35%",
          justifyContent: "flex-start",
          paddingLeft: 8,
        }}
        text={getChoiceDisplayName(choice?.name?.toString())}
        onPress={() => {
          setFieldOpen((prev) => {
            menuToggle(!prev);
            return !prev;
          });
        }}
      />
      <ContextMenu
        style={{ zIndex: 1, left: 0, top: 32 }}
        visible={fieldOpen}
        setVisible={setFieldOpen}
        ignoreRef={fieldBtnRef}
        onDismiss={() => menuToggle(false)}
      >
        {choices.map((c) => (
          <Button
            style={styles.menuButton}
            text={c.displayName || c.name}
            key={c.name}
            onPress={() => handlePickField(c.name)}
          />
        ))}
      </ContextMenu>

      <Button
        ref={dirBtnRef}
        iconPosition="right"
        style={{ gap: 8, backgroundColor: "transparent", minWidth: 100, marginLeft: 4, paddingLeft: 8 }}
        icon={choice?.direction === "ascending" ? "sort-ascending" : "sort-descending"}
        iconStyle={(state) => {
          return choice?.name ? { color: colors.text } : { color: colors.textSecondary };
        }}
        text={choice?.direction}
        onPress={() => {
          setExpOpen((prev) => {
            menuToggle(!prev);
            return !prev;
          });
        }}
        disabled={!choice?.name}
      />
      <ContextMenu
        style={{ left: 200, top: 32 }}
        visible={expOpen}
        setVisible={setExpOpen}
        ignoreRef={dirBtnRef}
        onDismiss={() => menuToggle(false)}
      >
        {["ascending", "descending"].map((op) => (
          <Button
            key={op}
            style={[styles.menuButton]}
            text={op}
            onPress={() => handlePickDirection(op as "ascending" | "descending")}
          />
        ))}
      </ContextMenu>

      {choice && (
        <Button
          icon="trash-can"
          iconSize={12}
          iconStyle={(state) => {
            return { color: state.hovered ? colors.text : colors.textSecondary };
          }}
          style={{ borderWidth: 0, backgroundColor: "transparent", minWidth: 25, maxHeight: 25 }}
          onPress={handleClear}
        />
      )}
    </View>
  );
}

// ---------- GroupByMenu ----------
export function GroupByMenu<FN extends string>({
  initial,
  choices,
  menuToggle,
  onChange,
}: {
  initial?: FN[];
  choices: { name: string; displayName?: string }[];
  menuToggle: (p?: boolean) => void;
  onChange?: (groupby?: FN[]) => void;
}) {
  const [fieldOpen, setFieldOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(initial ?? []);
  const { colors } = useTheme();
  const addBtnRef = useRef<View>(null);

  useEffect(() => {
    setSelected(initial ?? []);
  }, [initial]);

  const getDisplayName = (name: string) => {
    const found = choices.find((c) => c.name === name);
    return found?.displayName || name;
  };

  const availableChoices = choices.filter((c) => !selected.includes(c.name));

  const handleAdd = (field: string) => {
    const next = [...selected, field];
    setSelected(next);
    onChange?.(next as FN[]);
    setFieldOpen(false);
    menuToggle(false);
  };

  const handleRemove = (field: string) => {
    const next = selected.filter((f) => f !== field);
    setSelected(next);
    onChange?.(next.length > 0 ? (next as FN[]) : undefined);
  };

  return (
    <View style={{ flexDirection: "column", gap: 4, flexBasis: "100%" }}>
      {selected.map((field, i) => (
        <View key={field} style={{ flexDirection: "row", alignItems: "center", gap: 4, maxHeight: 30 }}>
          <ThemedText style={{ fontSize: 12, width: 18, textAlign: "center", color: colors.textSecondary }}>
            {i + 1}.
          </ThemedText>
          <Button
            style={{ backgroundColor: "transparent", flexGrow: 1, justifyContent: "flex-start", paddingLeft: 4 }}
            text={getDisplayName(field)}
          />
          <Button
            icon="trash-can"
            iconSize={12}
            iconStyle={(state) => ({ color: state.hovered ? colors.text : colors.textSecondary })}
            style={{ borderWidth: 0, backgroundColor: "transparent", minWidth: 25, maxHeight: 25 }}
            onPress={() => handleRemove(field)}
          />
        </View>
      ))}
      {availableChoices.length > 0 && (
        <View style={[{ flexDirection: "row", gap: 4, maxHeight: 30 }, fieldOpen && { elevation: 1, zIndex: 1 }]}>
          <Button
            ref={addBtnRef}
            icon="plus"
            iconSize={12}
            style={{ backgroundColor: "transparent", flexGrow: 1 }}
            text={selected.length === 0 ? "Select field" : "Add group level"}
            onPress={() => {
              setFieldOpen((prev) => {
                menuToggle(!prev);
                return !prev;
              });
            }}
          />
          <ContextMenu
            style={{ zIndex: 1, left: 0, top: 32 }}
            visible={fieldOpen}
            setVisible={setFieldOpen}
            ignoreRef={addBtnRef}
            onDismiss={() => menuToggle(false)}
          >
            {availableChoices.map((c) => (
              <Button
                style={styles.menuButton}
                text={c.displayName || c.name}
                key={c.name}
                onPress={() => handleAdd(c.name)}
              />
            ))}
          </ContextMenu>
        </View>
      )}
    </View>
  );
}

export function BooleanMenu({
  style,
  onSet,
  trueColor,
  falseColor,
}: {
  style?: StyleProp<ViewStyle>;
  onSet?: (val: boolean) => void;
  trueColor?: string;
  falseColor?: string;
}) {
  return (
    <View style={style}>
      <Button text="True" style={{ backgroundColor: trueColor || "transparent" }} onPress={() => onSet?.(true)} />
      <Button text="False" style={{ backgroundColor: falseColor || "transparent" }} onPress={() => onSet?.(false)} />
    </View>
  );
}

export function EnumMenu({
  style,
  onSet,
  options = [],
}: {
  style?: StyleProp<ViewStyle>;
  onSet?: (val: string) => void;
  options?: { value: string; color?: string }[];
}) {
  const { colors } = useTheme();
  return (
    <View style={[style, { maxHeight: 300 }]}>
      {options.map((option) => (
        <Button
          key={option.value}
          text={option.value}
          style={{
            backgroundColor: resolveColor(option.color, colors),
            marginVertical: 2,
          }}
          onPress={() => onSet?.(option.value)}
        />
      ))}
    </View>
  );
}

export function ArrayMenu({
  pickerType,
  selectedValues = [],
  readonlyValues = [],
  onSet,
  onCancel,
}: {
  pickerType: "fips" | "market" | "time_modifier" | "incident";
  selectedValues?: string[];
  readonlyValues?: string[];
  onSet?: (values: string[]) => void;
  onCancel?: () => void;
}) {
  if (pickerType === "fips") {
    return <FipsPicker selectedFipsCodes={selectedValues} onPick={(codes) => onSet?.(codes)} onCancel={onCancel} />;
  }

  if (pickerType === "time_modifier") {
    return (
      <TimeModifierEditor
        selectedModifiers={selectedValues as TimeModifierCode[]}
        onPick={(modifiers) => onSet?.(modifiers)}
        onCancel={onCancel}
      />
    );
  }

  return (
    <MultiMarketPicker
      selectedMarketIds={selectedValues}
      readonlyMarketIds={readonlyValues}
      onPick={(ids) => onSet?.(ids)}
      onCancel={onCancel}
    />
  );
}

const styles = StyleSheet.create({
  container: {},
  // Filter styles
  filterMenuContainer: {
    flexDirection: "column",
    gap: 4,
    paddingVertical: 4,
  },
  filterPill: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    borderRadius: 10,
    paddingVertical: 2,
    borderWidth: 1,
    minHeight: 28,
  },
  expressionBadge: {
    position: "absolute",
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: "white",
    fontSize: 11,
    fontWeight: "600",
  },
  expressionEditor: {
    padding: 12,
    gap: 8,
  },
  expressionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  operatorButton: {
    minWidth: 80,
    height: 28,
    paddingHorizontal: 8,
  },
  addConditionButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#ccc",
    marginTop: 4,
    gap: 4,
  },
  matchModeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  globalMatchModeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  matchModeButton: {
    minWidth: 40,
    height: 24,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
  },
  matchModeText: {
    fontSize: 11,
  },
  // Visible styles
  visibleMenuContainer: {
    flexDirection: "column",
    gap: 4,
    paddingVertical: 4,
  },
  visiblePill: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    borderRadius: 10,
    paddingVertical: 2,
    borderWidth: 1,
    minHeight: 28,
  },
  pillText: {
    fontSize: 13,
  },
  pillTextInactive: {
    textDecorationLine: "line-through",
  },
  menuButton: {
    gap: 8,
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "transparent",
    paddingHorizontal: 8,
    borderColor: "transparent",
  },
  // Enum expression styles
  enumExpressionContainer: {
    gap: 8,
    paddingVertical: 4,
  },
  enumOperatorRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  enumOptionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  enumOptionButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    minHeight: 28,
    borderWidth: 1,
    borderColor: "#ccc",
    backgroundColor: "transparent",
  },
  enumOptionText: {
    fontSize: 12,
  },
});
